<script setup lang="ts">
  /**
   * Form section — 03-components/15d-form-section · OSCloud DS 1.1.148
   *
   * The titled box a group of editable fields sits in. The box is the surface, the header strip
   * names the group, and the body holds the fields. The marker is this block's own recipe — a
   * 3px bar on the box's top-left edge, the height of the title line. It is not `BfCard`'s
   * accent stripe (a full-height edge for emphasis) and the header is not a card title bar (an
   * eyebrow on a tinted strip for a group that is only read).
   *
   * `foot` is an optional tinted strip on the bottom edge: the group's closing remark, or ONE
   * closing control. The test is rank, not element type — a field the group cannot be submitted
   * without, or one of a run of peers, is a body field; a field that is optional, secondary or
   * read *after* the group is filled in (an attachment, a reference number) is the closer, and
   * the tint is what says so. Never a Save / Cancel row: actions sit outside the box.
   */
  withDefaults(
    defineProps<{
      /** Names the group. Rendered in the header strip; omit it and the header is not rendered. */
      title?: string
      /** The id the page's `aria-labelledby` points at. */
      titleId?: string
      /** Heading level — the outline decides it, not this block. */
      titleTag?: 'h2' | 'h3' | 'h4'
      /** One supporting sentence under the title. */
      lead?: string
    }>(),
    { title: '', titleId: undefined, titleTag: 'h2', lead: '' },
  )
</script>

<template>
  <div class="form-section">
    <div v-if="title || $slots.title" class="form-section__header">
      <component :is="titleTag" :id="titleId" class="form-section__title">
        <slot name="title">{{ title }}</slot>
      </component>
      <p v-if="lead || $slots.lead" class="form-section__lead">
        <slot name="lead">{{ lead }}</slot>
      </p>
    </div>

    <div class="form-section__body">
      <slot />
    </div>

    <div v-if="$slots.foot" class="form-section__foot">
      <slot name="foot" />
    </div>
  </div>
</template>

<style scoped lang="scss">
  .form-section {
    /* Block-local: the lead gap, the marker's height and the foot's tint. The tint is the
       block's only colour hook — a section whose foot should sit on white sets it to a TOKEN,
       never a hex. */
    --bf-form-section-lead-gap: 5px;
    --bf-form-section-marker-height: 36px;
    --bf-form-section-foot-surface: var(--bf-blue-light);
    --bf-form-section-row-gap: var(--bf-sp-3);
    position: relative;
    /* The body's row stacks on the section's own width, so a section in a rail and one
       on a wide page behave the same. */
    container-type: inline-size;
    /* NOT overflow: hidden. The two things that would need clipping — the marker's corner
       and the foot's bottom corners — round themselves below, because a select's menu or
       any other popup opened inside the body must be able to leave the box. */
    border: 1px solid var(--bf-line);
    border-radius: var(--bf-r-lg);
    background: var(--bf-white);
  }

  .form-section__header {
    padding: 18px 28px 16px;
    border-bottom: 1px solid var(--bf-line-2);
  }

  /* The marker, positioned on the section so it sits on the box's edge. Its height equals the
     title's line box, so it reads as underlining the heading from the side, and it rounds its
     own top-left corner to the box's inner radius rather than being clipped by the box. */
  .form-section__header::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 3px;
    height: var(--bf-form-section-marker-height);
    /* The inner radius: the box's own, less its 1px hairline. */
    border-top-left-radius: calc(var(--bf-r-lg) - 1px);
    background: var(--bf-orange-dark);
  }

  /* The title is the group's, so a .required-marker that must read as part of the last
     word goes inside this one inline item rather than bare in the heading. */
  .form-section__title-text {
    display: inline;
  }

  .form-section__title {
    margin: 0;
    font-family: var(--bf-font-display);
    font-size: var(--bf-fs-base);
    font-weight: var(--bf-fw-semibold);
    line-height: var(--bf-lh-tight);
    letter-spacing: var(--bf-tr-tight);
    color: var(--bf-deep-blue);
  }

  .form-section__lead {
    margin: var(--bf-form-section-lead-gap) 0 0;
    font-family: var(--bf-font-body);
    font-size: var(--bf-fs-xs_5);
    line-height: var(--bf-lh-normal);
    color: var(--bf-fg-2);
  }

  .form-section__body {
    display: flex;
    flex-direction: column;
    gap: var(--bf-sp-3);
    padding: 22px 28px 24px;
  }

  /* Two equal fields on one line. Written by the consumer inside the body slot, so it is
     reached with :deep(). */
  .form-section__body :deep(.form-section__row) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--bf-form-section-row-gap);
    align-items: start;
  }

  /* Join rule: a .form-fieldset in the body keeps its meaning — fields that validate
     together, [data-invalid] for the group border — but drops its own surface and padding,
     because the section is the surface and a box in a box is never the picture. The invalid
     hairline comes back as a soft ring so the group-level error still shows. */
  .form-section__body > :deep(.form-fieldset) {
    padding: 0;
    background: transparent;
    border-color: transparent;
    border-radius: 0;
  }

  .form-section__body > :deep(.form-fieldset[data-invalid]) {
    padding: var(--bf-sp-2);
    border-color: var(--bf-form-invalid-hairline);
    border-radius: var(--bf-r-md);
  }

  /* The closing strip, mirroring a card's footer bar on this block's own padding: the hairline
     above separates it, and the tint says the control in it is read after the group is filled
     in. */
  .form-section__foot {
    display: flex;
    flex-direction: column;
    gap: var(--bf-sp-2);
    padding: 14px 28px 16px;
    border-top: 1px solid var(--bf-line-2);
    border-bottom-right-radius: calc(var(--bf-r-lg) - 1px);
    border-bottom-left-radius: calc(var(--bf-r-lg) - 1px);
    background: var(--bf-form-section-foot-surface);
  }

  /* Two equal fields stop fitting at 480px of section width. */
  @container (max-width: 480px) {
    .form-section__body :deep(.form-section__row) {
      grid-template-columns: 1fr;
    }
  }

  @media (max-width: 600px) {
    /* --bf-bp-sm: the side padding steps down to the page's narrow gutter. */
    .form-section__header {
      padding: 16px var(--bf-page-gutter-narrow) 14px;
    }

    .form-section__body {
      padding: 20px var(--bf-page-gutter-narrow);
    }

    .form-section__foot {
      padding: 14px var(--bf-page-gutter-narrow);
    }
  }
</style>
